import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import { ForwardedRef, useEffect, useRef } from 'react';

import useEmailPhoneAutocomplete from '../../hooks/useEmailPhoneAutocomplete';
import { mergeRefs } from '../../lib/utils';

import PhoneField from './PhoneField';
import TextField, { TextFieldProps } from './TextField';

import styles from '../../styles/fields/email-phone-field.module.css';

interface EmailPhoneFieldProps extends Omit<TextFieldProps, 'type' | 'onChange'> {
    type?: string | null;
    value?: string | null;
    placeholder?: string | null;
    required?: boolean;
    withoutAutocomplete?: boolean;
    onTypeChange?: ((type: string) => void) | null;
    onChange?: ((value: string | null) => void) | null;
    className?: string | null;
    [key: string]: unknown;
}

function EmailPhoneField({
    type = null,
    value: initialValue = null,
    placeholder = null,
    required = false,
    withoutAutocomplete = false,
    onTypeChange = null,
    onChange = null,
    onFocus = null,
    onBlur = null,
    className = null,
    ref: inputRef = null,
    ...props
}: EmailPhoneFieldProps & { ref?: ForwardedRef<HTMLInputElement> | null }) {
    const onAutocompleteChange = (newValue: string | null) => {
        if (onChange !== null) {
            const finalValue = newValue || null;
            onChange(finalValue);
        }
    };

    const {
        ref,
        value,
        suggestion = null,
        placeholder: customPlaceholder,
        onChange: onInputChange,
        setValue,
        type: fieldType = null,
    } = useEmailPhoneAutocomplete({
        value: initialValue,
        autocomplete: !withoutAutocomplete,
        onChange: onAutocompleteChange,
        onTypeChange,
        type,
        defaultType: 'email',
    });

    useEffect(() => {
        if (initialValue === null) {
            setValue(initialValue);
        }
    }, [initialValue]);

    const finalPlaceholder =
        !withoutAutocomplete && !isEmpty(value) ? suggestion || customPlaceholder : placeholder;

    const refFieldType = useRef(fieldType);

    useEffect(() => {
        if (ref.current !== null && refFieldType.current !== fieldType) {
            ref.current.focus();
            refFieldType.current = fieldType;
        }
    }, [fieldType]);

    return (
        <>
            {fieldType === 'tel' ? (
                <PhoneField
                    ref={mergeRefs(ref, inputRef)}
                    placeholder={finalPlaceholder}
                    value={value}
                    required={required}
                    onChange={onInputChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className={styles.phone}
                    inputClassName={classNames([styles.container, className])}
                    withBorder
                    {...props}
                />
            ) : (
                <TextField
                    ref={mergeRefs(ref, inputRef)}
                    type="email"
                    placeholder={finalPlaceholder}
                    value={value}
                    required={required}
                    onChange={onInputChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className={classNames([styles.container, className])}
                    withBorder
                    {...props}
                />
            )}
        </>
    );
}

export default EmailPhoneField;
